import { useId, type ReactElement } from 'react';
import { floorUnitsPerSeat } from '../../../domain/economics';
import { InfoPopover } from '../common/InfoPopover';
import {
  SEAT_CLASSES,
  SEAT_CLASS_SPECS,
  type AircraftType,
  type CabinConfiguration,
  type CabinMetrics,
  type SeatClass,
} from '../../../domain/models';
import {
  CLASS_LABELS,
  clampNumber,
  formatPitch,
  formatUnits,
  seatsOfClass,
} from './cabinFormat';
import { NumberField } from './NumberField';

export interface ClassControlsProps {
  readonly type: AircraftType;
  readonly cabin: CabinConfiguration;
  readonly metrics: CabinMetrics;
  readonly onSeatsChange: (seatClass: SeatClass, value: number) => void;
  readonly onEconomyPitchChange: (value: number) => void;
  readonly onBusinessPitchChange: (value: number) => void;
}

/** Pitch currently chosen for a class; fixed classes report their product pitch. */
function pitchOf(seatClass: SeatClass, cabin: CabinConfiguration): number {
  if (seatClass === 'ECONOMY') {
    return cabin.economyPitchInches;
  }
  if (seatClass === 'BUSINESS') {
    return cabin.businessPitchInches;
  }
  return SEAT_CLASS_SPECS[seatClass].defaultPitchInches;
}

/**
 * Largest seat count the slider offers for a class: what physically fits the
 * floor at the class's tightest pitch, capped by the certified limit.
 */
function sliderMaxSeats(type: AircraftType, seatClass: SeatClass): number {
  const spec = SEAT_CLASS_SPECS[seatClass];
  const perSeat = floorUnitsPerSeat(seatClass, spec.minPitchInches);
  const byFloor = perSeat > 0 ? Math.floor(type.cabinFloorUnits / perSeat) : 0;
  return Math.max(0, Math.min(byFloor, type.maxCertifiedSeats));
}

interface ClassRowProps extends ClassControlsProps {
  readonly seatClass: SeatClass;
}

function ClassRow({
  seatClass,
  type,
  cabin,
  metrics,
  onSeatsChange,
  onEconomyPitchChange,
  onBusinessPitchChange,
}: ClassRowProps): ReactElement {
  const uid = useId();
  const spec = SEAT_CLASS_SPECS[seatClass];
  const seats = seatsOfClass(cabin, seatClass);
  const pitch = pitchOf(seatClass, cabin);
  const pitchEditable = seatClass === 'ECONOMY' || seatClass === 'BUSINESS';
  const unitsPerSeat = floorUnitsPerSeat(seatClass, Number.isFinite(pitch) ? pitch : spec.defaultPitchInches);
  const seatMax = Math.max(sliderMaxSeats(type, seatClass), Number.isFinite(seats) ? seats : 0);
  const classUnits = metrics.floorUnitsByClass[seatClass];

  const onPitchChange = seatClass === 'ECONOMY' ? onEconomyPitchChange : onBusinessPitchChange;
  const seatsId = `${uid}-seats`;
  const seatsNumId = `${uid}-seats-num`;
  const pitchId = `${uid}-pitch`;
  const pitchNumId = `${uid}-pitch-num`;
  const pitchOutOfRange =
    pitchEditable && (pitch < spec.minPitchInches || pitch > spec.maxPitchInches);
  const seatsInvalid = !Number.isInteger(seats) || seats < 0;

  return (
    <fieldset className="ce-class" data-seat-class={seatClass}>
      <legend className="ce-class__legend">
        <span className="ce-class__swatch" data-seat-class={seatClass} aria-hidden="true" />
        {CLASS_LABELS[seatClass]}
      </legend>

      <div className="ce-class__row">
        <label className="ce-class__label" htmlFor={seatsId}>
          Seats
        </label>
        <input
          id={seatsId}
          className="ce-slider"
          type="range"
          min={0}
          max={seatMax}
          step={1}
          value={clampNumber(Number.isFinite(seats) ? seats : 0, 0, seatMax)}
          onChange={(event): void => onSeatsChange(seatClass, event.target.valueAsNumber)}
        />
        <NumberField
          id={seatsNumId}
          label={`${CLASS_LABELS[seatClass]} seat count`}
          value={seats}
          step={1}
          min={0}
          invalid={seatsInvalid}
          onCommit={(value): void => onSeatsChange(seatClass, value)}
        />
      </div>

      <div className="ce-class__row">
        <span className="ce-class__label-wrap">
          <span className="ce-class__label" id={`${pitchId}-label`}>
            Pitch
          </span>
          <InfoPopover topic="seatPitch" />
        </span>
        {pitchEditable ? (
          <>
            <input
              id={pitchId}
              className="ce-slider"
              type="range"
              min={spec.minPitchInches}
              max={spec.maxPitchInches}
              step={1}
              aria-labelledby={`${pitchId}-label`}
              value={clampNumber(Number.isFinite(pitch) ? pitch : spec.defaultPitchInches, spec.minPitchInches, spec.maxPitchInches)}
              onChange={(event): void => onPitchChange(event.target.valueAsNumber)}
            />
            <NumberField
              id={pitchNumId}
              label={`${CLASS_LABELS[seatClass]} pitch in inches`}
              value={pitch}
              step={1}
              min={spec.minPitchInches}
              max={spec.maxPitchInches}
              unit="″"
              invalid={pitchOutOfRange}
              onCommit={onPitchChange}
            />
          </>
        ) : (
          <span className="ce-class__fixed">
            {formatPitch(pitch)} <span className="ce-class__fixed-note">fixed</span>
          </span>
        )}
      </div>

      <p className="ce-class__meta">
        {formatUnits(unitsPerSeat)} units/seat
        {pitchEditable ? ` at ${formatPitch(pitch)}` : ''} · {formatUnits(classUnits)} units used
      </p>
    </fieldset>
  );
}

/** Seat count and pitch controls for all four cabin classes. */
export function ClassControls(props: ClassControlsProps): ReactElement {
  return (
    <section className="ce-card" aria-labelledby="ce-classes-title">
      <h3 id="ce-classes-title" className="ce-card__title">
        Seat classes
      </h3>
      <div className="ce-classes">
        {SEAT_CLASSES.map((seatClass) => (
          <ClassRow key={seatClass} seatClass={seatClass} {...props} />
        ))}
      </div>
    </section>
  );
}
